/**
 * Language Utility Functions
 *
 * Utilities for managing user language preferences with Firestore sync
 * Handles language detection, storage, and synchronization
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { SupportedLanguage, DEFAULT_LANGUAGE, isLanguageSupported } from '@/i18n';
import { handleQueryError } from '@/lib/error-handling';
import { logger } from '@/lib/logger';

// User language document interface
interface UserLanguageDoc {
  language: SupportedLanguage;
  updatedAt: number;
  source: 'user' | 'system' | 'browser';
}

/**
 * Get user language preference from Firestore
 * @param userId - User ID
 * @returns Promise<SupportedLanguage | null> - User's language preference or null if not found
 */
export const getUserLanguage = async (userId: string): Promise<SupportedLanguage | null> => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();
    const language = userData?.language;

    // Validate the language from Firestore
    if (language && isLanguageSupported(language)) {
      return language;
    }

    return null;
  } catch (error) {
    logger.warn('Failed to get user language from Firestore', { error, userId });
    return null;
  }
};

/**
 * Set user language preference in Firestore
 * @param userId - User ID
 * @param language - Language to set
 * @returns Promise<void>
 */
export const setUserLanguage = async (
  userId: string,
  language: SupportedLanguage
): Promise<void> => {
  try {
    if (!isLanguageSupported(language)) {
      throw new Error(`Unsupported language: ${language}`);
    }

    const userDocRef = doc(db, 'users', userId);
    const userLanguageData: UserLanguageDoc = {
      language,
      updatedAt: Date.now(),
      source: 'user',
    };

    // Use setDoc with merge to avoid overwriting other user data
    await setDoc(userDocRef, userLanguageData, { merge: true });
  } catch (error) {
    logger.warn(
      'Failed to set user language in Firestore - language preference will be stored in localStorage only',
      { error, userId, language }
    );

    throw handleQueryError(error, ['user', 'language', 'set']);
  }
};

/**
 * Update user language preference in Firestore
 * @param userId - User ID
 * @param language - New language
 * @returns Promise<void>
 */
export const updateUserLanguage = async (
  userId: string,
  language: SupportedLanguage
): Promise<void> => {
  try {
    if (!isLanguageSupported(language)) {
      throw new Error(`Unsupported language: ${language}`);
    }

    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      language,
      updatedAt: Date.now(),
      source: 'user',
    });
  } catch (error) {
    throw handleQueryError(error, ['user', 'language', 'update']);
  }
};

/**
 * Detect browser language and return supported language
 * @returns SupportedLanguage - Detected language or default
 */
export const detectBrowserLanguage = (): SupportedLanguage => {
  try {
    // Get browser language
    const browserLang = navigator.language || navigator.languages?.[0] || 'en';

    // Extract language code (e.g., 'en-US' -> 'en')
    const langCode = browserLang.split('-')[0].toLowerCase();

    // Check if it's supported
    if (isLanguageSupported(langCode)) {
      return langCode;
    }

    // Check if any of the user's preferred languages are supported
    if (navigator.languages) {
      for (const lang of navigator.languages) {
        const code = lang.split('-')[0].toLowerCase();
        if (isLanguageSupported(code)) {
          return code;
        }
      }
    }

    return DEFAULT_LANGUAGE;
  } catch (error) {
    logger.warn('Failed to detect browser language', { error });
    return DEFAULT_LANGUAGE;
  }
};

/**
 * Get language from localStorage
 * @returns SupportedLanguage | null - Stored language or null
 */
export const getStoredLanguage = (): SupportedLanguage | null => {
  try {
    const stored = localStorage.getItem('i18nextLng');
    if (stored && isLanguageSupported(stored)) {
      return stored;
    }
    return null;
  } catch (error) {
    logger.warn('Failed to get stored language', { error });
    return null;
  }
};

/**
 * Store language in localStorage
 * @param language - Language to store
 */
export const storeLanguage = (language: SupportedLanguage): void => {
  try {
    localStorage.setItem('i18nextLng', language);
  } catch (error) {
    logger.warn('Failed to store language', { error, language });
  }
};

/**
 * Get language preference with fallback chain:
 * 1. localStorage
 * 2. Firestore (if user is logged in)
 * 3. Browser language
 * 4. Default language
 * @param userId - Optional user ID for Firestore lookup
 * @returns Promise<SupportedLanguage> - Determined language
 */
export const getLanguagePreference = async (userId?: string): Promise<SupportedLanguage> => {
  try {
    // Check localStorage
    const storedLanguage = getStoredLanguage();
    if (storedLanguage) {
      return storedLanguage;
    }

    // Check Firestore if user is logged in
    if (userId) {
      const firestoreLanguage = await getUserLanguage(userId);
      if (firestoreLanguage) {
        // Store in localStorage for future use
        storeLanguage(firestoreLanguage);
        return firestoreLanguage;
      }
    }

    // Detect browser language
    const browserLanguage = detectBrowserLanguage();

    // Fallback to default
    return browserLanguage;
  } catch (error) {
    logger.warn('Failed to get language preference', { error, userId });
    return DEFAULT_LANGUAGE;
  }
};

/**
 * Sync language preference across all storage methods
 * @param userId - User ID
 * @param language - Language to sync
 * @returns Promise<void>
 */
export const syncLanguagePreference = async (
  userId: string,
  language: SupportedLanguage
): Promise<void> => {
  try {
    // Store in localStorage
    storeLanguage(language);

    // Store in Firestore
    await setUserLanguage(userId, language);
  } catch (error) {
    // If Firestore fails, at least localStorage should work
    logger.warn('Failed to sync language preference to Firestore', { error, userId, language });
    throw error;
  }
};

/**
 * Language preference change handler
 * Updates all storage methods when language changes
 * @param userId - User ID
 * @param language - New language
 * @returns Promise<void>
 */
export const handleLanguageChange = async (
  userId: string,
  language: SupportedLanguage
): Promise<void> => {
  try {
    await syncLanguagePreference(userId, language);
  } catch (error) {
    throw handleQueryError(error, ['language', 'change']);
  }
};
